import { ArrowLeft } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { checkinCustomer, createCustomer, getCustomerByPhone } from '../../api/customer.api';
import bgImage from '../../assets/img/checkin/background.jpg';
import errorIcon from '../../assets/img/checkin/error.png';
import logo from '../../assets/img/checkin/logo-checkin.png';
import styles from '../../assets/scss/pages/Checkin.module.scss';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuthStore } from '../../stores/authStore';

// Màn hình check-in cho khách tự nhập SĐT — đồng bộ với app (HomeCheckin / SignUp / Success)
// step: 'home' → (chưa có khách) 'signup' → 'success' → tự quay về 'home' sau 3s

const KEYS = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['⌫', '0', '✔'],
];

const SUCCESS_TIMEOUT = 3000;

const formatPhone = (value) => {
    const digits = (value || '').replace(/\D/g, '').slice(0, 10);
    if (digits.length === 0) return '';
    if (digits.length <= 4) return digits;
    if (digits.length <= 7) return digits.slice(0, 4) + '.' + digits.slice(4);
    return digits.slice(0, 4) + '.' + digits.slice(4, 7) + '.' + digits.slice(7, 10);
};

const formatPoint = (point) => point.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');

export default function CheckinPage() {
    const navigate = useNavigate();
    const logout = useAuthStore((state) => state.logout);
    const { role } = usePermissions();

    const [step, setStep] = useState('home');
    const [phone, setPhone] = useState('');
    const [name, setName] = useState('');
    const [customer, setCustomer] = useState(null);
    const [isError, setIsError] = useState(false);
    const [subMessage, setSubMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const showError = (msg) => {
        setIsError(true);
        setSubMessage(Array.isArray(msg) ? msg.join(', ') : msg);
    };

    const resetToHome = useCallback(() => {
        setPhone('');
        setName('');
        setCustomer(null);
        setStep('home');
    }, []);

    // ===== HOME =====
    const handlePress = useCallback((num) => {
        setPhone((prev) => (prev.length < 10 ? prev + num : prev));
    }, []);

    const handleDelete = useCallback(() => {
        setPhone((prev) => prev.slice(0, -1));
    }, []);

    const handleSubmit = useCallback(async () => {
        if (submitting) return;
        if (phone.length < 10) {
            alert('số điện thoại chưa đủ 10 ký tự');
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await getCustomerByPhone(phone);
            if (data.message) {
                setStep('signup');
            } else {
                setCustomer(data);
                setStep('success');
            }
        } catch (error) {
            console.error('❌ Lỗi API:', error);
            showError(error.response?.data?.message || 'Lỗi kết nối đến server!');
        } finally {
            setSubmitting(false);
        }
    }, [phone, submitting]);

    const handleKey = (num) => {
        if (num === '⌫') handleDelete();
        else if (num === '✔') handleSubmit();
        else handlePress(num);
    };

    // Hỗ trợ bàn phím vật lý ở màn nhập SĐT
    useEffect(() => {
        if (step !== 'home' || isError) return undefined;
        const onKeyDown = (e) => {
            if (/^\d$/.test(e.key)) handlePress(e.key);
            else if (e.key === 'Backspace') handleDelete();
            else if (e.key === 'Enter') handleSubmit();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [step, isError, handlePress, handleDelete, handleSubmit]);

    const handleBack = () => {
        if (role === 'checkin') return;
        navigate('/admin/dashboard', { replace: true });
    };

    const handleLogout = () => {
        logout();
        navigate('/admin/login', { replace: true });
    };

    // ===== SIGNUP =====
    const handleFinish = async (e) => {
        e?.preventDefault();
        if (submitting) return;
        if (name.trim() === '') {
            alert('Tên không được để trống!');
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await createCustomer({ phone, name });
            setCustomer(data);
            setStep('success');
        } catch (err) {
            console.log('Lỗi kết nối server!', err);
            showError(err.response?.data?.message || 'Lỗi kết nối đến server!');
        } finally {
            setSubmitting(false);
        }
    };

    // ===== SUCCESS =====
    // Ref chặn gọi check-in 2 lần (StrictMode chạy effect 2 lần ở dev)
    const checkedInPhoneRef = useRef(null);

    useEffect(() => {
        if (step !== 'success') return undefined;

        if (checkedInPhoneRef.current !== phone) {
            checkedInPhoneRef.current = phone;
            checkinCustomer({ phone }).catch((error) => {
                console.error('Lỗi check-in:', error.response?.data?.message || error.message);
            });
        }

        const timeout = setTimeout(() => {
            checkedInPhoneRef.current = null;
            resetToHome();
        }, SUCCESS_TIMEOUT);

        return () => clearTimeout(timeout);
    }, [step, phone, resetToHome]);

    return (
        <div
            className={styles.page}
            style={{ backgroundImage: `linear-gradient(rgba(6, 18, 32, 0.5), rgba(6, 18, 32, 0.5)), url(${bgImage})` }}
        >
            {step === 'home' && (
                <>
                    <div className={styles.topBar}>
                        {role && role !== 'checkin' ? (
                            <button type="button" onClick={handleBack} className={styles.outlineBtn}>
                                <ArrowLeft size={24} />
                            </button>
                        ) : (
                            <span />
                        )}
                        <button type="button" onClick={handleLogout} className={styles.outlineBtn}>
                            <span className={styles.outlineBtnTitle}>Đăng xuất</span>
                        </button>
                    </div>

                    <div className={styles.content}>
                        <div className={styles.logo}>
                            <img src={logo} alt="Navo" className={styles.logoImage} />
                            <span className={styles.titleLogo}>Navo-Sổ tiệm</span>
                        </div>
                        <p className={styles.desLogo}>Vui lòng nhập số điện thoại của bạn để checkin ...</p>
                        <div className={styles.phoneInput}>{formatPhone(phone)}</div>

                        <div className={styles.keyboard}>
                            {KEYS.map((row, index) => (
                                <div key={index} className={styles.row}>
                                    {row.map((num) => (
                                        <button
                                            key={num}
                                            type="button"
                                            className={styles.buttonNum}
                                            onClick={() => handleKey(num)}
                                            disabled={num === '✔' && submitting}
                                        >
                                            {num}
                                        </button>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}

            {step === 'signup' && (
                <>
                    <div className={styles.topBar}>
                        <button type="button" onClick={resetToHome} className={styles.outlineBtn}>
                            <ArrowLeft size={24} />
                        </button>
                    </div>

                    <form className={styles.content} onSubmit={handleFinish}>
                        <div className={styles.logo}>
                            <img src={logo} alt="Navo" className={styles.logoImage} />
                            <span className={styles.titleLogo}>HappyCheckin</span>
                        </div>
                        <p className={styles.desLogo}>Vui lòng nhập tên hoặc biệt danh của bạn ...</p>

                        <div className={styles.wrapperInput}>
                            <input
                                className={styles.nameInput}
                                placeholder="Nhập tên của bạn ..."
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                autoFocus
                            />
                        </div>
                        <button type="submit" className={styles.finishBtn} disabled={submitting}>
                            Hoàn thành
                        </button>
                    </form>
                </>
            )}

            {step === 'success' && (
                <div className={styles.content}>
                    <div className={styles.iconContainer}>
                        <svg height="80" width="80" viewBox="0 0 48 48">
                            <circle cx="24" cy="24" r="22" fill="#31c331" />
                            <path
                                d="M14 24l6 6 14-14"
                                stroke="white"
                                strokeWidth="4"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                fill="none"
                            />
                        </svg>
                    </div>

                    <span className={styles.thankText}>Thank you!</span>
                    <span className={styles.name}>{customer?.name || ''}</span>
                    <span className={styles.checkinText}>Bạn đã checkin thành công!</span>

                    <div className={styles.line} />

                    <span className={styles.currentPoint}>Điểm hiện của bạn là</span>
                    <span className={styles.points}>{formatPoint(customer?.point || 0)} Pts</span>
                </div>
            )}

            {isError && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modalBox}>
                        <img src={errorIcon} alt="" className={styles.modalIcon} />
                        <span className={styles.modalTitle}>Thông báo!</span>
                        {subMessage ? <span className={styles.modalSubText}>{subMessage}</span> : null}
                        <button
                            type="button"
                            className={styles.modalCloseBtn}
                            onClick={() => {
                                setIsError(false);
                                setSubMessage('');
                            }}
                        >
                            Đóng
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
